import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { openCustomerPortal } from '../lib/plans';
import { useAuthStore } from '../stores/authStore';
import { useToast } from './Toast';
import type { Workspace } from '../types/database';

// Shown while Stripe retries a failed payment (past_due): the workspace is still Paid, but
// locks if the retries run out. Only the user who pays for the Subscription sees it, since
// only their Customer's portal can update the card. Staff viewing as a User see it on any
// past_due workspace (they can't read other Users' Subscriptions), with the button stubbed.
export function PaymentRetryingBanner({ workspace }: { workspace: Workspace }) {
  const { user, viewingAs } = useAuthStore();
  const toast = useToast();
  const [opening, setOpening] = useState(false);
  const subscriptionId = workspace.subscription_status === 'past_due' ? workspace.stripe_subscription_id : null;

  // Users can only read their own Subscriptions, so a row means this user pays for it.
  const { data: isPayer } = useQuery({
    queryKey: ['isSubscriptionPayer', subscriptionId, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.from('subscriptions').select('id').eq('id', subscriptionId!).maybeSingle();
      if (error) throw error;
      return !!data;
    },
    enabled: !!subscriptionId && !!user && !viewingAs,
  });

  if (!subscriptionId || !(isPayer || viewingAs)) return null;

  const handleUpdatePayment = async () => {
    if (viewingAs) {
      toast.info('Read-only: only the payer can update payment details.');
      return;
    }
    if (!user) return;
    setOpening(true);
    const url = await openCustomerPortal(user.id, window.location.href);
    if (url) {
      window.location.href = url;
      return;
    }
    toast.error('Unable to open your billing details right now. Please try again.');
    setOpening(false);
  };

  return (
    <div className="trial-ended-banner">
      <div className="trial-ended-banner-copy">
        <div className="trial-ended-banner-copy-main">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/>
            <line x1="12" x2="12" y1="8" y2="12"/>
            <line x1="12" x2="12.01" y1="16" y2="16"/>
          </svg>
          <div className="trial-ended-banner-copy-text">
            <div className="text-size-regular text-weight-semibold">Your last payment failed</div>
            <div className="text-size-small text-weight-regular text-colour-opacity-80">
              We're retrying it, but update your payment details to keep tracking without interruption
            </div>
          </div>
        </div>
      </div>
      <button
        type="button"
        className="button is-small is-white w-button"
        onClick={handleUpdatePayment}
        disabled={opening}
      >
        {opening ? 'Opening...' : 'Update payment details'}
      </button>
    </div>
  );
}
